import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  FileText, User, Briefcase, ArrowRight, Sparkles, MoreVertical, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { PIPELINE_STAGES, REJECTED_STAGE, ALL_STAGES, normalizeStatus, getStageConfig } from '@/lib/pipelineStages';

const ApplicationsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({ stages: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const isRecruiter = ['super_admin', 'client_admin', 'team_leader', 'recruiter'].includes(user?.role);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, countsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers, withCredentials: true }),
        axios.get(`${API}/applications/pipeline/counts`, { headers, withCredentials: true }),
      ]);
      // Normalise legacy statuses on client too
      const apps = (appsRes.data.applications || []).map(a => ({ ...a, status: normalizeStatus(a.status) }));
      setApplications(apps);
      setCounts(countsRes.data || { stages: {}, total: 0 });
    } catch { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const changeStatus = async (app, newStatus) => {
    if (app.status === newStatus) return;
    setUpdatingId(app.app_id);
    try {
      await axios.put(
        `${API}/applications/${app.app_id}/status?status=${newStatus}`,
        {},
        { headers, withCredentials: true },
      );
      toast.success(`Moved to ${ALL_STAGES.find(s => s.key === newStatus)?.label}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
    setUpdatingId(null);
  };

  const groupByStatus = () => {
    const groups = Object.fromEntries(ALL_STAGES.map(s => [s.key, []]));
    applications.forEach(a => {
      const key = normalizeStatus(a.status);
      if (groups[key]) groups[key].push(a);
    });
    return groups;
  };

  const groups = groupByStatus();
  const filteredApps = statusFilter === 'all'
    ? applications
    : applications.filter(a => normalizeStatus(a.status) === statusFilter);

  const StatusBadge = ({ status }) => {
    const cfg = getStageConfig(status);
    const Icon = cfg.icon;
    return (
      <Badge className={`border ${cfg.badge}`} data-testid={`status-badge-${status}`}>
        <Icon className="w-3 h-3 mr-1" /> {cfg.label}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fadeIn" data-testid="applications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {isRecruiter ? 'Candidate Pipeline' : 'My Applications'}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {counts.total} total {counts.total === 1 ? 'candidate' : 'candidates'}
              {statusFilter !== 'all' && (
                <> · Filtered by <strong className="text-slate-900">{ALL_STAGES.find(s => s.key === statusFilter)?.label}</strong>{' '}
                  <button className="text-indigo-600 underline text-xs ml-1" onClick={() => setStatusFilter('all')}>clear</button>
                </>
              )}
            </p>
          </div>
          {!isRecruiter && (
            <Link to="/jobs">
              <Button data-testid="browse-jobs-btn">
                <Briefcase className="w-4 h-4 mr-2" /> Browse Jobs
              </Button>
            </Link>
          )}
        </div>

        {/* Pipeline strip (like screenshot) */}
        {isRecruiter && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="pipeline-strip">
            {PIPELINE_STAGES.map(stage => {
              const active = statusFilter === stage.key;
              const count = counts.stages?.[stage.key] ?? 0;
              return (
                <button
                  key={stage.key}
                  type="button"
                  onClick={() => setStatusFilter(active ? 'all' : stage.key)}
                  className={`${stage.stripBg} ${stage.stripText} px-4 py-3 rounded-lg text-left transition-all hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 flex items-center justify-between gap-2 ${active ? 'ring-2 ring-offset-2 ring-slate-800 scale-[1.02]' : ''}`}
                  data-testid={`pipeline-stage-${stage.key}`}
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">{stage.label}</p>
                  </div>
                  <span className={`text-xs font-bold ${stage.stripCountBg} px-2.5 py-1 rounded-full min-w-[36px] text-center flex-shrink-0`}>
                    {count.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {isRecruiter ? (
          // Kanban view for recruiters
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {ALL_STAGES.map(stage => {
                const apps = groups[stage.key] || [];
                // Hide rejected if filter is not 'all' and not 'rejected'
                if (statusFilter !== 'all' && statusFilter !== stage.key) return null;
                return (
                  <div key={stage.key} className="w-72 flex-shrink-0" data-testid={`kanban-col-${stage.key}`}>
                    <div className={`bg-slate-50 border-t-4 ${stage.columnAccent} rounded-lg p-3 min-h-[200px]`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <stage.icon className="w-4 h-4 text-slate-500" />
                          <h3 className="font-semibold text-slate-700 text-sm">{stage.label}</h3>
                        </div>
                        <Badge variant="secondary" className="text-xs">{apps.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {apps.map(app => (
                          <Card
                            key={app.app_id}
                            className="cursor-pointer hover:shadow-md transition-shadow group"
                            data-testid={`pipeline-card-${app.app_id}`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div
                                  className="flex items-center gap-2 min-w-0 flex-1"
                                  onClick={() => navigate(`/applications/${app.app_id}`)}
                                >
                                  <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                                    {app.candidate?.picture ? (
                                      <img src={app.candidate.picture} alt="" className="w-7 h-7 rounded-full" />
                                    ) : (
                                      <User className="w-3.5 h-3.5 text-slate-500" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-xs text-slate-900 truncate">{app.candidate?.name || 'Candidate'}</p>
                                    <p className="text-[11px] text-slate-500 truncate">{app.candidate?.email}</p>
                                  </div>
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="p-1 rounded hover:bg-slate-100 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                      data-testid={`card-move-${app.app_id}`}
                                      disabled={updatingId === app.app_id}
                                    >
                                      <MoreVertical className="w-3.5 h-3.5" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Move to</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {PIPELINE_STAGES.filter(s => s.key !== stage.key).map(s => (
                                      <DropdownMenuItem
                                        key={s.key}
                                        onClick={() => changeStatus(app, s.key)}
                                        data-testid={`move-${app.app_id}-${s.key}`}
                                      >
                                        <s.icon className="w-3.5 h-3.5 mr-2" /> {s.label}
                                      </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => changeStatus(app, 'rejected')}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`move-${app.app_id}-rejected`}
                                    >
                                      <REJECTED_STAGE.icon className="w-3.5 h-3.5 mr-2" /> Rejected
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              <div
                                className="mt-2 cursor-pointer"
                                onClick={() => navigate(`/applications/${app.app_id}`)}
                              >
                                <p className="text-xs font-medium text-indigo-600 line-clamp-1">{app.job?.title || '—'}</p>
                                {app.match_score != null && (
                                  <div className="flex items-center gap-1 mt-1" data-testid={`match-score-${app.app_id}`}>
                                    <Sparkles className="w-3 h-3 text-amber-500" />
                                    <span className={`text-[11px] font-semibold ${
                                      app.match_score >= 70 ? 'text-green-600' :
                                      app.match_score >= 40 ? 'text-yellow-600' :
                                      'text-slate-500'
                                    }`}>{app.match_score}% match</span>
                                  </div>
                                )}
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Applied {new Date(app.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {apps.length === 0 && (
                          <div className="text-center py-6 text-slate-400 text-xs">No candidates</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Candidate: simple list
          <>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Filter className="w-5 h-5 text-slate-400" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48" data-testid="status-filter">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Applications</SelectItem>
                      {ALL_STAGES.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : filteredApps.length > 0 ? (
                filteredApps.map(app => (
                  <Link key={app.app_id} to={`/applications/${app.app_id}`}>
                    <Card className="card-hover" data-testid={`application-card-${app.app_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <StatusBadge status={app.status} />
                            </div>
                            <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit' }}>
                              {app.job?.title || 'Unknown Job'}
                            </h3>
                            <p className="text-slate-600">{app.job?.location}</p>
                            <p className="text-sm text-slate-500 mt-2">
                              Applied on {new Date(app.created_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric',
                              })}
                            </p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No applications found</h3>
                    <p className="text-slate-500 mb-4">
                      {statusFilter !== 'all'
                        ? 'Try changing the filter'
                        : "You haven't applied to any jobs yet"}
                    </p>
                    <Link to="/jobs"><Button>Browse Jobs</Button></Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ApplicationsPage;
